import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

export function Stages({ stages, current, status, message }) {
  const idx = Math.max(0, stages.findIndex((s) => s.key === current));
  const failed = status === "failed";
  return (
    <div className="space-y-4" data-testid="generation-stages">
      <ol className="space-y-2">
        {stages.map((s, i) => {
          const done = status === "completed" || i < idx;
          const active = i === idx && status !== "completed" && !failed;
          return (
            <li key={s.key} className={cn("flex items-center gap-3 text-sm transition-colors", done ? "text-foreground" : active ? "text-foreground" : "text-muted-foreground/60")}>
              <span className={cn("h-6 w-6 rounded-full border flex items-center justify-center text-[10px] font-mono-ls shrink-0", done ? "bg-gold border-gold text-black" : active ? "border-gold text-gold pulse-gold" : "border-border")}>{done ? <Check className="h-3.5 w-3.5" /> : i + 1}</span>
              <span className={cn(active && "font-medium")}>{s.label}</span>
              {active && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-gold animate-pulse" />}
            </li>
          );
        })}
      </ol>
      <motion.p key={message} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className={cn("text-sm font-display italic", failed ? "text-destructive" : "text-muted-foreground")} data-testid="generation-message">{message}</motion.p>
    </div>
  );
}

export function Eyebrow({ children, className }) {
  return <p className={cn("eyebrow-label", className)}>{children}</p>;
}

export function SectionTitle({ eyebrow, title, body, action, className }) {
  return (
    <div className={cn("flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4", className)}>
      <div className="max-w-2xl">
        {eyebrow && <Eyebrow className="mb-2">{eyebrow}</Eyebrow>}
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl leading-[1.1]">{title}</h2>
        {body && <p className="text-muted-foreground mt-2 text-sm sm:text-base">{body}</p>}
      </div>
      {action}
    </div>
  );
}

export function Reveal({ children, delay = 0, className, once = true }) {
  return (
    <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once, margin: "-40px" }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay }} className={className}>
      {children}
    </motion.div>
  );
}

export function EmptyExample({ title, body, image, cta }) {
  return (
    <div className="card-lux overflow-hidden grid md:grid-cols-[1.1fr_1fr]" data-testid="empty-example">
      <div className="relative min-h-[240px]">
        <img src={image} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <span className="absolute left-4 top-4 eyebrow-label bg-background/80 glass px-2 py-1 rounded">Example</span>
      </div>
      <div className="p-8 flex flex-col justify-center gap-4">
        <h3 className="font-display text-2xl">{title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
        {cta}
      </div>
    </div>
  );
}
